import axios from 'axios';

// New Sales Dashboard API calls — standalone, does not reuse or modify the
// existing Services/Dashboard.js used by the old SalesDashboard.jsx.
//
// Backend: SALES-APP-BACKEND-NODE-API, routes/salesDashboard.js
// Base URL comes from env (same service the SmartFlo/agreement calls use),
// never hardcoded — see REACT_APP_PAYMENT_LINK_URL in .env.
const BASE_URL = process.env.REACT_APP_PAYMENT_LINK_URL;

// GET /salesDashboard/overview?userId=&startDate=&endDate= (epoch ms)
export async function getDashboardOverview(userId, startDate, endDate) {
  const { data } = await axios.get(`${BASE_URL}salesDashboard/overview`, {
    params: { userId, startDate, endDate },
  });
  return data; // { success, data: {...} } — caller reads response.data
}

// GET /salesDashboard/siteVisits?userId=&startDate=&endDate= (epoch ms)
export async function getDashboardSiteVisits(userId, startDate, endDate) {
  const { data } = await axios.get(`${BASE_URL}salesDashboard/siteVisits`, {
    params: { userId, startDate, endDate },
  });
  return data; // { success, data: {...} } — caller reads response.data
}

// GET /salesDashboard/leadsCalls?userId=&startDate=&endDate= (epoch ms)
export async function getDashboardLeadsCalls(userId, startDate, endDate) {
  const { data } = await axios.get(`${BASE_URL}salesDashboard/leadsCalls`, {
    params: { userId, startDate, endDate },
  });
  return data; // { success, data: {...} } — caller reads response.data
}

// GET /salesDashboard/tokens?userId=&startDate=&endDate=&heldStatus=&heldPage= (epoch ms)
// heldStatus (multi-select — an array, possibly empty) filters the "held
// longest" table's Status column, heldPage pages it — both applied
// server-side against the full matching set, not just a capped slice — see
// tokensController.js. Axios serializes an array param as heldStatus[]=a&
// heldStatus[]=b (and omits it entirely when empty), which express/qs parses
// back into an array on the other end.
export async function getDashboardTokens(userId, startDate, endDate, heldStatus, heldPage) {
  const { data } = await axios.get(`${BASE_URL}salesDashboard/tokens`, {
    params: { userId, startDate, endDate, heldStatus: heldStatus || [], heldPage: heldPage || undefined },
  });
  return data; // { success, data: {...} } — caller reads response.data
}

// GET /salesDashboard/allotments?userId=&startDate=&endDate=&project=&cp=&fos=&status=&page= (epoch ms)
// Each filter is multi-select (an array of picked values, possibly empty)
// and applied server-side against the full matching dataset, so results
// (and totalRows) are accurate, not just whichever rows happened to already
// be capped — see allotmentsController.js.
export async function getDashboardAllotments(userId, startDate, endDate, filters) {
  const { data } = await axios.get(`${BASE_URL}salesDashboard/allotments`, {
    params: { userId, startDate, endDate, ...(filters || {}) },
  });
  return data; // { success, data: {...} } — caller reads response.data
}

// GET /salesDashboard/agreements?userId=&startDate=&endDate=&project=&cp=&fos=&page= (epoch ms)
// Same server-side multi-select filter+page pattern as Allotments — see
// agreementsController.js.
export async function getDashboardAgreements(userId, startDate, endDate, filters) {
  const { data } = await axios.get(`${BASE_URL}salesDashboard/agreements`, {
    params: { userId, startDate, endDate, ...(filters || {}) },
  });
  return data; // { success, data: {...} } — caller reads response.data
}

// GET /salesDashboard/performance?userId=&startDate=&endDate= (epoch ms)
export async function getDashboardPerformance(userId, startDate, endDate) {
  const { data } = await axios.get(`${BASE_URL}salesDashboard/performance`, {
    params: { userId, startDate, endDate },
  });
  return data; // { success, data: {...} } — caller reads response.data
}

// GET /salesDashboard/persona?userId=&startDate=&endDate= (epoch ms)
export async function getDashboardPersona(userId, startDate, endDate) {
  const { data } = await axios.get(`${BASE_URL}salesDashboard/persona`, {
    params: { userId, startDate, endDate },
  });
  return data; // { success, data: {...} } — caller reads response.data
}

// GET /salesDashboard/personaDrilldown?userId=&startDate=&endDate=&dimension=&value= (epoch ms)
export async function getDashboardPersonaDrilldown(userId, startDate, endDate, dimension, value) {
  const { data } = await axios.get(`${BASE_URL}salesDashboard/personaDrilldown`, {
    params: { userId, startDate, endDate, dimension, value },
  });
  return data; // { success, data: {...} } — caller reads response.data
}

// GET /salesDashboard/projects?userId=&startDate=&endDate= (epoch ms)
export async function getDashboardProjects(userId, startDate, endDate) {
  const { data } = await axios.get(`${BASE_URL}salesDashboard/projects`, {
    params: { userId, startDate, endDate },
  });
  return data; // { success, data: {...} } — caller reads response.data
}
